import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";

import Alert from "@material-ui/lab/Alert";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import FormControl from "@material-ui/core/FormControl";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import IconButton from "@material-ui/core/IconButton";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Paper from "@material-ui/core/Paper";
import Select from "@material-ui/core/Select";
import Snackbar from "@material-ui/core/Snackbar";
import Switch from "@material-ui/core/Switch";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import TextField from "@material-ui/core/TextField";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import EmailOutlinedIcon from "@material-ui/icons/EmailOutlined";

const basePath =
  process.env.NODE_ENV === "production" ? "/edge" : "http://localhost:3010";

// Region list mirrors EMAIL_CAPTURE_REGIONS in
// app/src/components/home/NotifySignupForm.js (same grouping).
const REGION_OPTIONS = [
  { value: "eu-central-1", label: "Frankfurt (live)" },
  { value: "eu-west-2", label: "London (live)" },
  { value: "us-east-2", label: "Ohio (live)" },
  { value: "sa-east-1", label: "São Paulo (planned)" },
  { value: "ap-southeast-1", label: "Singapore (planned)" },
  { value: "upcoming-na-west", label: "US West (planned)" },
  { value: "upcoming-eu-north", label: "Stockholm / Northern EU (planned)" },
  { value: "upcoming-apac-tokyo", label: "Tokyo (planned)" },
  { value: "upcoming-apac-sydney", label: "Sydney (planned)" },
  { value: "upcoming-mena", label: "MENA / Dubai (planned)" },
  { value: "other", label: "Other / not listed" },
];

function getRegionLabel(value) {
  const found = REGION_OPTIONS.find((r) => r.value === value);
  return found ? found.label.replace(/\s*\((live|planned)\)\s*$/, "").trim() : value;
}

function getRegionDisplayName(value) {
  if (value === "other") return "your region";
  return getRegionLabel(value);
}

function getInitialSubject(lead) {
  if (!lead || lead.region === "other") return "";
  const name = getRegionDisplayName(lead.region);
  return `Ouro is now live in ${name}`;
}

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: "#1C1E20",
    minHeight: "100vh",
  },
  title: {
    fontSize: "1.8rem",
    fontWeight: 700,
    color: "#ffffff",
    marginBottom: theme.spacing(1),
  },
  subtitle: {
    fontSize: "0.9rem",
    color: "rgba(255,255,255,0.55)",
    marginBottom: theme.spacing(3),
  },
  filters: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
    alignItems: "center",
  },
  tableContainer: {
    backgroundColor: "#262829",
    borderRadius: 12,
  },
  tableHead: {
    "& th": {
      fontWeight: 700,
      color: "rgba(255,255,255,0.7)",
      borderBottom: "1px solid rgba(255,255,255,0.1)",
    },
  },
  row: {
    "& td": {
      color: "rgba(255,255,255,0.85)",
      borderBottom: "1px solid rgba(255,255,255,0.06)",
    },
  },
  mutedText: {
    color: "rgba(255,255,255,0.45)",
    fontSize: "0.82rem",
  },
  confirmedCheck: {
    color: "#4caf50",
    fontWeight: 700,
  },
  dialogPaper: {
    backgroundColor: "#1e2023",
    color: "#fff",
    minWidth: 480,
  },
  dialogTitle: {
    color: "#fff",
  },
  dialogInput: {
    "& .MuiOutlinedInput-root": {
      color: "#fff",
      "& fieldset": { borderColor: "rgba(255,255,255,0.2)" },
      "&:hover fieldset": { borderColor: "rgba(255,255,255,0.4)" },
    },
    "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.6)" },
  },
  footerNote: {
    fontSize: "0.78rem",
    color: "rgba(255,255,255,0.45)",
    marginTop: theme.spacing(1),
  },
}));

function relativeTime(dateStr) {
  if (!dateStr) return "Never";
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 2) return "Just now";
  if (minutes < 60) return `${minutes} minutes ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days > 1 ? "s" : ""} ago`;
  const months = Math.floor(days / 30);
  return `${months} month${months > 1 ? "s" : ""} ago`;
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function MarketingLeadsPage({ catalog }) {
  const classes = useStyles();
  const user = catalog?.user;

  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [regionFilter, setRegionFilter] = useState("");
  const [confirmedOnly, setConfirmedOnly] = useState(true);

  const [dialogLead, setDialogLead] = useState(null);
  const [dialogSubject, setDialogSubject] = useState("");
  const [dialogBody, setDialogBody] = useState("");
  const [dialogSending, setDialogSending] = useState(false);
  const [dialogError, setDialogError] = useState(null);

  const [snackbar, setSnackbar] = useState(null);

  const fetchLeads = useCallback(async () => {
    if (!user?.userId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (regionFilter) params.set("region", regionFilter);
      params.set("confirmedOnly", confirmedOnly ? "true" : "false");
      params.set("limit", rowsPerPage);
      params.set("skip", page * rowsPerPage);

      const url = `${basePath}/api/v7/admin/marketing-leads/adpass/${user.userId}?${params}`;
      const resp = await fetch(url, { credentials: "include" });
      const data = await resp.json();
      if (data.ok) {
        setLeads(data.items || []);
        setTotal(data.total || 0);
      }
    } catch (err) {
      console.error("Failed to fetch marketing leads", err);
    } finally {
      setLoading(false);
    }
  }, [user, page, rowsPerPage, regionFilter, confirmedOnly]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  function openDialog(lead) {
    setDialogLead(lead);
    setDialogSubject(getInitialSubject(lead));
    setDialogBody("");
    setDialogError(null);
    setDialogSending(false);
  }

  function closeDialog() {
    setDialogLead(null);
  }

  async function handleSendEmail() {
    if (!dialogSubject.trim() || !dialogBody.trim()) return;
    setDialogSending(true);
    setDialogError(null);
    try {
      const resp = await fetch(
        `${basePath}/api/v7/admin/marketing-leads/${dialogLead._id}/email/adpass/${user.userId}`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ subject: dialogSubject, body: dialogBody }),
        }
      );
      const data = await resp.json();
      if (data.ok) {
        closeDialog();
        setSnackbar(`Email sent to ${dialogLead.email}.`);
        fetchLeads();
      } else {
        setDialogError(data.message || "Something went wrong. Try again.");
      }
    } catch (_) {
      setDialogError("Something went wrong. Try again.");
    } finally {
      setDialogSending(false);
    }
  }

  return (
    <div className={classes.root}>
      <Typography className={classes.title}>Marketing leads</Typography>
      <Typography className={classes.subtitle}>
        People who asked to be notified when their region opens. Single opt-in — confirmed leads
        have clicked their confirmation email.
      </Typography>

      <Box className={classes.filters}>
        <FormControl variant="outlined" size="small" style={{ minWidth: 220 }}>
          <InputLabel style={{ color: "rgba(255,255,255,0.5)" }}>Region</InputLabel>
          <Select
            value={regionFilter}
            onChange={(e) => {
              setRegionFilter(e.target.value);
              setPage(0);
            }}
            label="Region"
            style={{ color: "#fff" }}
          >
            <MenuItem value="">All regions</MenuItem>
            {REGION_OPTIONS.map((r) => (
              <MenuItem key={r.value} value={r.value}>
                {r.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControlLabel
          control={
            <Switch
              checked={confirmedOnly}
              onChange={(e) => {
                setConfirmedOnly(e.target.checked);
                setPage(0);
              }}
              color="primary"
            />
          }
          label={<span style={{ color: "rgba(255,255,255,0.8)" }}>Confirmed only</span>}
        />
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress style={{ color: "#7C3AED" }} />
        </Box>
      ) : (
        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table>
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Region</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Signed up</TableCell>
                <TableCell>Confirmed</TableCell>
                <TableCell>Last emailed</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {leads.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" style={{ color: "rgba(255,255,255,0.4)", padding: "2rem" }}>
                    No leads found.
                  </TableCell>
                </TableRow>
              ) : (
                leads.map((lead) => {
                  const isUnconfirmed = !lead.confirmedAt;
                  const actionDisabled = isUnconfirmed && confirmedOnly;

                  return (
                    <TableRow key={lead._id} className={classes.row}>
                      <TableCell>{lead.email}</TableCell>
                      <TableCell>
                        <div>{getRegionLabel(lead.region)}</div>
                        {lead.regionOther && (
                          <div className={classes.mutedText}>{lead.regionOther}</div>
                        )}
                      </TableCell>
                      <TableCell>{lead.source || "—"}</TableCell>
                      <TableCell>
                        <Tooltip title={formatDate(lead.createdAt)}>
                          <span>{relativeTime(lead.createdAt)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {lead.confirmedAt ? (
                          <Tooltip title={formatDate(lead.confirmedAt)}>
                            <span className={classes.confirmedCheck}>✓</span>
                          </Tooltip>
                        ) : (
                          <span className={classes.mutedText}>—</span>
                        )}
                      </TableCell>
                      <TableCell>{relativeTime(lead.lastEmailedAt)}</TableCell>
                      <TableCell>
                        <Tooltip
                          title={
                            actionDisabled
                              ? "Lead hasn't confirmed their email — flip \"Confirmed only\" off in your filters if you really want to email anyway."
                              : `Email ${lead.email}`
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              disabled={actionDisabled}
                              onClick={() => openDialog(lead)}
                              style={{ color: actionDisabled ? "rgba(255,255,255,0.2)" : "#A78BFA" }}
                            >
                              <EmailOutlinedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100]}
            style={{ color: "rgba(255,255,255,0.7)" }}
          />
        </TableContainer>
      )}

      {/* Per-lead email dialog */}
      <Dialog
        open={Boolean(dialogLead)}
        onClose={closeDialog}
        PaperProps={{ className: classes.dialogPaper }}
        maxWidth="sm"
        fullWidth
      >
        {dialogLead && (
          <>
            <DialogTitle className={classes.dialogTitle}>
              Email {dialogLead.email}
            </DialogTitle>
            <DialogContent>
              {dialogLead && !dialogLead.confirmedAt && (
                <Alert severity="warning" style={{ marginBottom: 16 }}>
                  This lead hasn't confirmed their email. Sending may hurt our SES sender
                  reputation. Continue only if you have a specific reason.
                </Alert>
              )}
              {dialogError && (
                <Alert severity="error" style={{ marginBottom: 16 }}>
                  {dialogError}
                </Alert>
              )}
              <TextField
                label="Subject"
                value={dialogSubject}
                onChange={(e) => setDialogSubject(e.target.value)}
                required
                fullWidth
                variant="outlined"
                size="small"
                disabled={dialogSending}
                className={classes.dialogInput}
                style={{ marginBottom: 16 }}
              />
              <TextField
                label="Body"
                value={dialogBody}
                onChange={(e) => setDialogBody(e.target.value)}
                required
                fullWidth
                multiline
                rows={10}
                variant="outlined"
                disabled={dialogSending}
                className={classes.dialogInput}
              />
              <Typography className={classes.footerNote}>
                Sent from no-reply@ouro.is via SES. Replies go to the configured reply-to address.
              </Typography>
            </DialogContent>
            <DialogActions style={{ padding: "12px 24px" }}>
              <Button onClick={closeDialog} disabled={dialogSending} style={{ color: "rgba(255,255,255,0.6)" }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSendEmail}
                disabled={dialogSending || !dialogSubject.trim() || !dialogBody.trim()}
              >
                {dialogSending ? "Sending…" : "Send email"}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        message={snackbar}
      />
    </div>
  );
}

MarketingLeadsPage.propTypes = {
  catalog: PropTypes.object.isRequired,
};
